import type {
  QuestionGroupProps,
  Answer,
} from "../../../../../../components/Questionnaire/Section/Question/types";
import { isQuestion } from "../../../../../../components/Questionnaire/Section/Question/types";
import type { VisibilityMap } from "../../../../../../components/Questionnaire/validation/form/types";
import type { Answers } from "../../../../types";
import type { FlattenGroupResult } from "../types";
import { getPrimaryAnswer, createEmptyFlattenResult } from "../utils";
import { SecondaryText } from "../utils/SecondaryText";

/**
 * consolidated_question_group
 * Includes the primary question and group questions with visibility and answers adjusted based on the primary question's answer.
 */
export const flattenYesNoToggleGroup = (
  group: Partial<QuestionGroupProps>,
  answers: Answers,
  visibility: VisibilityMap,
): FlattenGroupResult => {
  const { primaryQuestion, groupQuestions } = group;
  const filteredQuestions = groupQuestions?.filter(isQuestion) ?? [];

  // If the primary or group questions are missing, or all the questions are hidden, return an empty flatten group
  if (
    !primaryQuestion ||
    !groupQuestions ||
    filteredQuestions.every(q => q.visible === false)
  ) {
    return createEmptyFlattenResult(answers, visibility);
  }

  const primaryAnswer = getPrimaryAnswer(primaryQuestion, answers);

  // Map questions with answers and set visibility based on primary answer
  const answersByQuestionName = { ...answers };
  const visibilityByQuestionName = { ...visibility };

  const questionsWithVisibility = filteredQuestions.map(q => {
    let answer: Answer | null = null;

    // When primary is explicitly "no", automatically set all children to "no"
    if (primaryAnswer === "no") {
      answer = "no";
    }

    /**
     * Answer resolution priority for yes/no toggle groups:
     * 1. Question's own answer (from API/initial state) - q.answer
     * 2. No default (null) - optional questions can remain unanswered
     *
     * Note: We don't check answers[q.name] or provide a default "no" because
     * optional child questions should only be answered if explicitly provided.
     */
    if (primaryAnswer === "yes") {
      answer = q.answer || null;
    }

    // Only set answer if it's not null (allows optional questions to remain unanswered)
    if (answer !== null) {
      answersByQuestionName[q.name] = answer;
    }

    visibilityByQuestionName[q.name] =
      primaryAnswer === "yes" ? !!q.visible : false;

    // Include answers based on visibility
    return { ...q, includeAnswer: !!q.visible };
  });

  // Update primary question's secondary text with the generated React element or preserve original
  const primaryQuestionWithSecondaryText = {
    ...primaryQuestion,
    secondaryText: SecondaryText({
      primaryQuestion,
      questions: filteredQuestions,
    }),
  };

  // Merge primary question and flattened group questions
  return {
    questions: [primaryQuestionWithSecondaryText, ...questionsWithVisibility],
    groups: {
      [primaryQuestion.name]: {
        kind: "group",
        names: questionsWithVisibility.map(q => q.name),
      },
    },
    visibility: visibilityByQuestionName,
    answers: {
      ...answersByQuestionName,
      [primaryQuestion.name]: primaryAnswer || "",
    },
  };
};
